<template>
  <div
    v-tooltip.top="{ value: isDisabled ? button.disabledTooltip : '', disabled: !isDisabled }"
    data-test-id="widgets-buttons"
    class="widget-btn p-button"
    :class="{ 'widget-btn--disabled': isDisabled }"
  >
    <div class="widget-btn__icon" :style="iconColor ? { '--widget-icon-color': iconColor } : {}">
      <img v-if="button.iconImage" :src="button.iconImage" alt="" class="widget-btn__icon-img" />
      <em v-else :class="button.iconClass" :style="iconColor ? { color: iconColor } : {}" />
    </div>
    <span class="widget-btn__label">{{ typeof button.label === 'function' ? button.label() : button.label }}</span>
    <Chip
      v-if="featurePermission && !featurePermission.access"
      :label="translation(`offers.plans.${featurePermission.minimumPlan}`)"
      class="widget-btn__chip"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
} from 'vue';
import Chip from 'primevue/chip';
import {
  hasAccessToFeatures,
} from '@/composables/User';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'WidgetsPanelButton',
  components: {
    Chip,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    button: {
      type: Object,
      required: true,
    },

    translation: {
      type: Function,
      required: true,
    },

    iconColor: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup(props) {
    const featurePermission = computed(() => hasAccessToFeatures(props.button.type));

    const isDisabled = computed(() => {
      if (props.button.disabled && typeof props.button.disabled === 'function') {
        return props.button.disabled();
      }
      return false;
    });

    return {
      featurePermission,
      isDisabled,
    };
  },
});
</script>

<style lang="scss" scoped>
.widget-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: grab;
  transition: all 0.2s ease;
  position: relative;

  &:active {
    cursor: grabbing;
    transform: scale(0.95);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    transition: all 0.2s ease;

    em {
      font-size: 1.25rem;
      color: #607D8B;
      transition: color 0.2s ease;
    }
  }

  &__icon-img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  &__label {
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
    text-align: center;
    color: #495057;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__chip {
    position: absolute;
    top: -4px;
    right: -4px;
    font-size: 9px;
    font-weight: 600;
    padding: 0 6px;
    height: 18px;
    line-height: 18px;
  }

  &:hover:not(.widget-btn--disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

    .widget-btn__icon em {
      color: var(--widget-icon-color, #94c840);
      filter: brightness(0.85);
    }
  }

  &--disabled {
    opacity: 0.45;
    cursor: not-allowed;

    * {
      pointer-events: none;
    }

    &:hover {
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
}
</style>
